import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Output, EventEmitter, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthError, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { EMPTY, merge } from 'rxjs';
import { catchError, delay, shareReplay, switchMap, take } from 'rxjs/operators';
import { ConfigService } from '../services/config.service';
import { SessionExpiredComponent } from '../shared/components/session-expired/session-expired.component';
import { ActiveDirectoryUserInfo } from '../shared/models/active-directory-userinfo.model';
import { Authorization_GetRoles_Options } from '../shared/models/authorization.model';
import { LoggedinUserInfo } from '../shared/models/loggedin-userinfo.model';
import { PrinceUserInfo } from '../shared/models/prince-userinfo.model';
import { Role, RoleValue } from '../shared/models/role.model';
import { User } from '../shared/models/user';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    adUserInfo: ActiveDirectoryUserInfo;
    princeUserInfo: PrinceUserInfo;
    headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    apiUrl = '';
    account: any;
    accessToken: any;
    loggedinUserInfo: LoggedinUserInfo;
    @Output() userLoaded: EventEmitter<User> = new EventEmitter<User>();
    public authenticated = false;
    public hasRoles: boolean = false;
    public user: User;

    public sessionExpired = false;

    private readonly sessionExpiredDialogDuration = 50000;
    private readonly dialog = inject(MatDialog);

    constructor(
        private httpClient: HttpClient,
        private msalService: MsalService,
        private configSvc: ConfigService) {
        this.apiUrl = this.configSvc.getApiUrl();
    }

    async initializeUser(account: AccountInfo): Promise<User | null> {
        this.account = account;

        if (!this.account) {
            this.user = null;
            this.authenticated = false;
            return null;
        }

        this.user = new User();

        const emailAddress =
            (this.account.username as string)
            ?? (this.account.idTokenClaims?.preferred_username as string)
            ?? (this.account.idTokenClaims?.upn as string)
            ?? '';

        const displayName =
            this.account.name
            ?? (this.account.idTokenClaims?.name as string)
            ?? emailAddress;

        this.user.email = emailAddress;
        this.user.displayName = displayName;

        try {
            const userInfo = await this.getUserInfo(emailAddress).toPromise();

            if (!userInfo) {
                this.authenticated = false;
                return null;
            }

            this.user.id = userInfo.UserId;

            const roles: any[] = userInfo.Roles ?? [];
            this.user.roles = roles.map(r => ({
                AuthorizationRoleID: r.Id,
                AuthorizationRoleName: r.Description
            }));

            if (userInfo.UserCompanies) {
                this.user.userCompanies = userInfo.UserCompanies;
            }

            this.hasRoles = this.user.roles.length > 0;
            this.authenticated = true;
            this.userLoaded.emit(this.user);

            return this.user;
        } catch (error) {
            console.error('initializeUser failed', error);
            this.user = null;
            this.authenticated = false;
            return null;
        }
    }

    async GetUserRoles(emailAddress) {
        const options: Authorization_GetRoles_Options = {
            AuthenticatedEmailAddress: emailAddress
        };
        const userRoles = await this.Authorization_GetRoles(options).toPromise().then(roles => {
            return roles;
        }).catch((error) => {
            console.error(error);
        });
        return userRoles;

    }

    public async GetRoles(user: User) {
        const options: Authorization_GetRoles_Options = {
            AuthenticatedEmailAddress: user.email
        };
        return this.Authorization_GetRoles(options).toPromise();
    }

    //Client method to get roles from Prince database
    public Authorization_GetRoles(Authorization_GetRoles_byEmail: Authorization_GetRoles_Options) {
        return this.httpClient.post<any>(`${this.apiUrl}/v1.0/Authorization/GetRoles`, Authorization_GetRoles_byEmail).pipe(
            catchError(error => {
                console.log(error);
                return EMPTY;
            }),
            shareReplay()
        );
    }

    private getUserInfo(emailAddress: string) {
        return this.httpClient.get<any>(`${this.apiUrl}/userinfo/GetUserInfo?email=${emailAddress}`);
    }

    // Silently request an access token
    public async getAccessToken(): Promise<string> {
        return this.acquireAccessToken(this.configSvc.scopes);
    }

    public async getAccessTokenForAPI(): Promise<string> {
        return this.acquireAccessToken(this.configSvc.scopeForAPI);
    }

    private async acquireAccessToken(scopes: string[]): Promise<string> {
        const account = this.account
            ?? this.msalService.instance.getActiveAccount()
            ?? this.msalService.instance.getAllAccounts()[0];

        if (!account) {
            if (this.authenticated) {
                this.handleSessionExpired();
            }

            return Promise.reject('No active MSAL account.');
        }

        this.account = account;

        const params: SilentRequest = {
            scopes: scopes,
            account: this.account
        };

        try {
            const authResponse = await this.msalService.acquireTokenSilent(params).toPromise();
            return authResponse.accessToken;
        } catch (error) {
            if (this.isSessionExpiredError(error)) {
                this.handleSessionExpired();
            }

            throw error;
        }
    }

    private isSessionExpiredError(error: unknown): boolean {
        if (error instanceof InteractionRequiredAuthError) {
            return true;
        }

        if (!(error instanceof AuthError)) {
            return false;
        }

        const transientErrorCodes = [
            'network_error',
            'post_request_failed',
            'no_network_connectivity',
            'interaction_in_progress'
        ];

        return !transientErrorCodes.includes(error.errorCode);
    }

    public notifyTokenAcquisitionFailure(error: unknown): void {
        if (!this.isSessionExpiredError(error)) {
            return;
        }

        this.handleSessionExpired();
    }

    public handleSessionExpired(): void {
        if (this.sessionExpired) {
            return;
        }

        this.sessionExpired = true;
        this.clearSessionState();

        const dialogRef = this.dialog.open(SessionExpiredComponent, {
            disableClose: true,
            data: {
                title: 'Session Expired',
                messageString: 'Your session has ended. You will be redirected to the sign in page.',
                duration: this.sessionExpiredDialogDuration
            }
        });

        const afterOpenWithDelay$ = dialogRef.afterOpened().pipe(delay(this.sessionExpiredDialogDuration));
        const afterClosed$ = dialogRef.afterClosed();

        merge(afterClosed$, afterOpenWithDelay$)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.dialog.closeAll();
                    void this.redirectToSignIn();
                }
            });
    }

    public clearSessionState(): void {
        this.account = null;
        this.user = null;
        this.authenticated = false;
        this.hasRoles = false;
        localStorage.removeItem('isAuthorized');
        sessionStorage.clear();
    }

    public logout(): void {
        this.clearSessionState();

        this.msalService.logoutRedirect()
            .subscribe({
                error: () => {
                    window.location.href = window.location.origin;
                }
            });
    }

    private async redirectToSignIn(): Promise<void> {
        try {
            await this.msalService.instance.clearCache();
        } catch (error) {
            console.error('clearCache failed', error);
        }

        this.msalService.loginRedirect()
            .subscribe({
                error: () => {
                    window.location.href = window.location.origin;
                }
            });
    }

    public async getUser(): Promise<User> {
        if (!this.authenticated) return null;
        let graphClient = Client.init({
            // Initialize the Graph client with an auth
            // provider that requests the token from the
            // auth service
            authProvider: async (done) => {
                let token = await this.getAccessToken()
                    .catch((reason) => {
                        done(reason, null);
                    });

                if (token) {
                    done(null, token);
                } else {
                    done("Could not get an access token", null);
                }
            }
        });

        // Get the user from Graph (GET /me)
        let graphUser = await graphClient.api('/me').get();

        let user = new User();

        if (graphUser != undefined) {
            user.displayName = graphUser.displayName;
            user.surname = graphUser.surname;
            user.id = graphUser.id;
            user.givenName = graphUser.givenName;

            // Prefer the userPrincipalName property, but fall back to mail
            user.email = graphUser.userPrincipalName || graphUser.mail;
        }
        return user;
    }


    loadRoles(): void {
        const roleOptions = {
            AuthenticatedEmailAddress: this.user.email
        } as Authorization_GetRoles_Options;

        this.Authorization_GetRoles(roleOptions).toPromise()
            .then(roles => this.user.roles = roles);
    }

    public async getRolesWithRefresh(): Promise<any[]> {
        const freshUser = await this.getUser();

        if (freshUser == null) {
            return null;
        }

        const wrappedEmail = {
            AuthenticatedEmailAddress: freshUser.email
        };

        if (this.user!.email == freshUser.email && this.user!.roles && this.user!.roles.length > 0) {
            return Promise.resolve(this.user!.roles);
        }

        return this.Authorization_GetRoles(wrappedEmail)
            .toPromise()
            .then(roles => {

                this.user.roles = roles;
                this.user.email = freshUser.email;
                this.user.displayName = freshUser.displayName;

                return roles;
            });
    }

    public async reload(): Promise<User | null> {
        if (this.user?.roles?.length) {
            this.authenticated = true;
            return this.user;
        }

        const account = this.msalService.instance.getActiveAccount()
            ?? this.msalService.instance.getAllAccounts()[0];

        if (!account) {
            this.account = null;
            this.user = null;
            this.authenticated = false;
            return null;
        }

        this.msalService.instance.setActiveAccount(account);

        try {
            const user = await this.initializeUser(account);
            this.authenticated = !!user;
            return user;
        } catch (error) {
            console.error(error);
            this.account = null;
            this.user = null;
            this.authenticated = false;
            return null;
        }
    }

    public UserInRole(roleEnum: RoleValue): boolean {
        if (!this.user) {
            return false;
        }
        var index: number = this.user.roles.findIndex(role => +role.AuthorizationRoleID == roleEnum);
        return (index > -1 ? true : false);
    }

    public UserInRoles(roleValues: RoleValue[]): boolean {
        if (!this.user) {
            return false;
        }
        const allowedStatusRoles = this.user.roles.filter(role => roleValues.includes(+role.AuthorizationRoleID));
        return (allowedStatusRoles.length > 0);
    }
}

